/**
 * AddMenu — "+ Nytt" popover for creating new items from the sidebar.
 *
 * Matches v1's AddButtonMenu design:
 * - Add To-Do (N) — with Plus icon + description
 * - New Project (P) — with pie TimelineIcon + description
 * - New List (L) — with list TimelineIcon + description
 * - New Area (A) — with cube icon + description
 *
 * Each option shows an icon, title, description, and keyboard shortcut.
 * v1 parity: rounded-[20px] menu, rounded-[15px] items, generous padding,
 * description text, conditional keyboard shortcuts on larger screens.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { StaticIcon, type IconMode } from "@/shared/icons/StaticIcon"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { ObjectStatus } from "@/db/schema"
import Plus from "@/shared/icons/pikaicons/stroke/maths/plus-default.svg?react"
import ChevronDown from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-down.svg?react"
import { cn } from "@/shared/utils/general/cn"

// ---------------------------------------------------------------------------
// Menu option definitions — matches v1 AddButtonMenu items
// ---------------------------------------------------------------------------

interface MenuOption {
  id: string
  label: string
  description: string
  shortcut: string
  icon: React.ReactNode
}

function getMenuOptions(): MenuOption[] {
  return [
    {
      id: "todo",
      label: "Add To-Do",
      description: "Quickly add a to-do to your inbox",
      shortcut: "N",
      icon: (
        <StaticIcon
          type="plus"
          size={24}
          mode="monochrome"
        />
      ),
    },
    {
      id: "project",
      label: "New Project",
      description: "Define a goal, then work towards\nit one to-do at a time.",
      shortcut: "P",
      icon: (
        <DynamicIcon
          type="project"
          percentage={33}
          unfinished={0}
          status={ObjectStatus.Unfinished}
          color="var(--muted-foreground)"
          size="small"
        />
      ),
    },
    {
      id: "list",
      label: "New List",
      description:
        "Compile items or tasks for\neasy organization and planning.",
      shortcut: "L",
      icon: (
        <DynamicIcon
          type="list"
          percentage={33}
          unfinished={0}
          status={ObjectStatus.Unfinished}
          color="var(--muted-foreground)"
          size="small"
        />
      ),
    },
    {
      id: "area",
      label: "New Area",
      description:
        "Group projects and to-dos based\non different responsibilities, such\nas Family or Work",
      shortcut: "A",
      icon: <StaticIcon type="area" size={24} mode="monochrome" />,
    },
  ]
}

// ---------------------------------------------------------------------------
// AddMenu component
// ---------------------------------------------------------------------------

interface AddMenuProps {
  iconMode: IconMode
  onAction?: (type: "todo" | "project" | "list" | "area") => void
  /** Render as a circular blue FAB with Plus+Chevron (mobile sidebar) */
  fabMode?: boolean
}

export const AddMenu = memo(function AddMenu({
  iconMode,
  onAction,
  fabMode,
}: AddMenuProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const options = getMenuOptions()

  // Close on click outside
  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  // Close on Escape + keyboard shortcuts while menu is open
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false)
        buttonRef.current?.focus()
        return
      }
      const key = e.key.toUpperCase()
      const option = options.find((o) => o.shortcut === key)
      if (option) {
        e.preventDefault()
        handleSelect(option.id)
      }
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [open, options])

  const handleSelect = useCallback(
    (id: string) => {
      setOpen(false)
      onAction?.(id as "todo" | "project" | "list" | "area")
    },
    [onAction],
  )

  return (
    <div className="relative">
      {/* Trigger button — text mode (sidebar footer) or FAB mode (mobile sidebar) */}
      {fabMode ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={t("New")}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg active:scale-95 transition-transform"
        >
          <Plus className="h-7 w-7" />
          <ChevronDown className="h-4 w-4 -ml-1" />
        </button>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-foreground",
            iconMode === "color"
              ? "hover:bg-muted-foreground/10"
              : "hover:bg-accent",
          )}
        >
          <StaticIcon type="plus" size={20} mode={iconMode} />
          <span>{t("New")}</span>
        </button>
      )}

      {/* Popover menu — positioned above the button, matching v1 style */}
      {open && (
        <div
          ref={menuRef}
          className={cn(
            "absolute z-50",
            fabMode
              ? "bottom-full right-0 mb-3"
              : "bottom-full left-0 mb-2",
            "w-75 rounded-[20px] border border-border bg-popover p-1.5 shadow-lg",
            "animate-in fade-in-0 slide-in-from-bottom-2 duration-150",
          )}
          role="menu"
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="menuitem"
              data-action={`add-${option.id}`}
              onClick={() => handleSelect(option.id)}
              className={cn(
                "relative flex w-full items-start gap-3 rounded-[15px] px-3 pt-2.5 pb-4",
                "text-left transition-colors hover:bg-accent",
              )}
            >
              {/* Icon — aligned to top of first text line */}
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center">
                {option.icon}
              </span>

              {/* Label + description */}
              <span className="mr-6 min-w-0 flex-1">
                <span className="block text-sm font-semibold text-foreground">
                  {t(option.label)}
                </span>
                <span className="mt-0.5 block whitespace-pre-wrap text-xs text-muted-foreground">
                  {t(option.description)}
                </span>
              </span>

              {/* Keyboard shortcut — hidden on small screens (matching v1) */}
              <kbd className="absolute right-4 top-2.5 hidden text-[0.7em] font-normal text-muted-foreground desk:block">
                {option.shortcut}
              </kbd>
            </button>
          ))}
        </div>
      )}
    </div>
  )
})
